import { SurveyTheme, SurveyQuestion, fieldValidation, Survey, LanguageCode, surveyHelper } from "../../../amorg-common";
import { updateSurveyQuestion, invertSurveyQuestion, moveSurveyQuestion, deleteSurveyQuestion, updateSurveyQuestionText } from "../../../api/surveyClientApi";
import { TextAreaSaveField } from "../../../designSystem/formField/textArea/TextAreaSaveField";
import React from "react";
import { FlexItem, Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { IndicatorEditor } from "./IndicatorEditor";
import { ListRow } from "../../../designSystem/compositeComponents/ListRow";
import { OnOff } from "../../../designSystem/inputs/OnOff";
import { ToolBarButton } from "../../../designSystem/compositeComponents/ButtonToolBar";
import { TooltipWrapper } from "../../../designSystem/TooltipWrapper";
import { Icon } from "../../../designSystem/icons/Icons";
import { TextAvatar } from '../../../designSystem/avatar/TextAvatar';

type QuestionListItemProps = {
    survey: Survey,
    languageCode: LanguageCode,
    originalQuestionIndex: number,
    surveyId: string,
    themes: SurveyTheme[],
    themeIndex: number,
    questionIndex: number,
    question: SurveyQuestion,
    questionsInThisTheme: number,
    allIndicators: string[],
    disableEdit: boolean,
    onQuestionUpdated: () => void;
};
export function QuestionListItem (props: QuestionListItemProps): JSX.Element
{
    async function handleChangeTitle (newValue: string)
    {
        const result = await updateSurveyQuestionText(
            props.surveyId,
            props.themeIndex,
            props.questionIndex,
            props.languageCode,
            newValue
        );
        if (result.isSuccess)
        {
            props.onQuestionUpdated();
        }
    }

    async function handleChangeCanComment (_: string, checked: boolean)
    {
        const result = await updateSurveyQuestion(
            props.surveyId,
            props.themeIndex,
            props.questionIndex,
            props.question.indicators,
            props.question.title,
            checked
        );
        if (result.isSuccess)
        {
            props.onQuestionUpdated();
        }
    }

    async function handleChangeInvert (_: string, checked: boolean)
    {
        const result = await invertSurveyQuestion(
            props.surveyId,
            props.themeIndex,
            props.questionIndex,
            checked
        );
        if (result.isSuccess)
        {
            props.onQuestionUpdated();
        }
    }

    async function handleMoveQuestion (move: 'up' | 'down')
    {

        // handle between themes

        if (move === 'up')
        {

            if (props.questionIndex == 0 && props.themeIndex > 0)
            {
                const themeIndexAbove = props.themeIndex - 1;
                const questionsInThemeAbove = props.themes[themeIndexAbove].questions.length;
                await moveSurveyQuestion(props.surveyId, props.themeIndex, props.questionIndex, themeIndexAbove, questionsInThemeAbove);
                props.onQuestionUpdated();
            }

            if (props.questionIndex > 0)
            {
                await moveSurveyQuestion(props.surveyId, props.themeIndex, props.questionIndex, props.themeIndex, props.questionIndex - 1);
                props.onQuestionUpdated();
            }
        }

        if (move === 'down')
        {

            const allThemesLength = props.themes.length - 1;

            if (props.questionIndex == props.questionsInThisTheme - 1 && props.themeIndex < allThemesLength)
            {
                await moveSurveyQuestion(props.surveyId, props.themeIndex, props.questionIndex, props.themeIndex + 1, props.questionIndex * 0);
                props.onQuestionUpdated();
            }

            if (props.questionIndex < props.questionsInThisTheme - 1)
            {
                await moveSurveyQuestion(props.surveyId, props.themeIndex, props.questionIndex, props.themeIndex, props.questionIndex + 1);
                props.onQuestionUpdated();
            }
        }
    }

    async function handleDeleteQuestion ()
    {
        const result = await deleteSurveyQuestion(props.surveyId, props.themeIndex, props.questionIndex);
        if (result.isSuccess)
        {
            props.onQuestionUpdated();
        }
    }

    return (
        <ListRow styleVariant="neutral" level="level2">
            <Flex gap={10}>
                <Flex>
                    <TextAvatar text={`${props.originalQuestionIndex + 1}`} size="42px"/>
                </Flex>
                <Flex flexDirection="column" gap={24} style={{ flexGrow: 1 }}>

                    <Flex flexDirection="row" justifyContent="stretch" gap={16}>


                        <FlexItem flexGrow="1">
                            <TextAreaSaveField
                                label=""
                                value={surveyHelper.getQuestionValue(props.survey, props.themeIndex, props.questionIndex, props.languageCode)}
                                onSave={(value, valid) =>
                                {
                                    if (valid)
                                        handleChangeTitle(value);
                                }}
                                validator={(currentValue) =>
                                {
                                    return fieldValidation.surveyQuestion.title(currentValue)
                                        ? { valid: true }
                                        : { valid: false, errorMessage: `Ugyldig titel. Skal udfyldes med maks. 400 (${currentValue.length}) tegn.` };
                                }}
                                disabled={props.disableEdit}
                                minRows={1} />
                        </FlexItem>
                    </Flex>

                    <Flex gap={16}>
                        <Flex alignItems="center" gap={8}>
                            <span>Vis kommentarfelt</span>
                            <OnOff active={props.question.canComment} onClick={(active) => handleChangeCanComment('', active)} disabled={props.disableEdit} />
                        </Flex>
                        <Flex alignItems="center" gap={8}>
                            <span>Omvendt rapportværdi</span>
                            <OnOff active={props.question.invertScore === true} onClick={(active) => handleChangeInvert('', active)} />
                            <TooltipWrapper tooltip={<div>Normalt er en besvarelse god hvis der vælges <i>Meget enig</i> og dårlig hvis der vælges <i>Meget uenig</i>.<br />
                                Ved at vælge omvendt rapport værdi, bliver &quot;Meget enig&quot; til et dårligt svar (værdi 1), og &quot;Meget uenig&quot; til et godt svar (værdi 5).<br />
                                <br />
                                Denne indstilling kan ændres selvom der er oprettet besvarelser.</div>}>
                                <Icon name="help" />
                            </TooltipWrapper>

                            {/* <Toggletip>
                                Normalt er en besvarelse god hvis der vælges <i>Meget enig</i> og dårlig hvis der vælges <i>Meget uenig</i>.<br />
                                Ved at vælge omvendt rapport værdi, bliver &quot;Meget enig&quot; til et dårligt svar (værdi 1), og &quot;Meget uenig&quot; til et godt svar (værdi 5).<br />
                                <br />
                                Denne indstilling kan ændres selvom der er oprettet besvarelser.
                            </Toggletip> */}
                        </Flex>
                    </Flex>

                    <IndicatorEditor
                        survey={props.survey}
                        themeIndex={props.themeIndex}
                        questionIndex={props.questionIndex}
                        allIndicators={props.allIndicators}
                        selectedIndicators={props.question.indicators}
                        disableEdit={props.disableEdit}
                        onChange={props.onQuestionUpdated} />



                    {!props.disableEdit &&
                        <Flex justifyContent="flex-end">
                            <ToolBarButton
                                icon="arrows_arrow_arrowDown"
                                variant="text"
                                onClick={() => handleMoveQuestion('down')}
                                tooltip="Flyt spørgsmål ned"
                                disabled={props.disableEdit} />
                            <ToolBarButton
                                icon="arrows_arrow_arrowUp"
                                variant="text"
                                onClick={() => handleMoveQuestion('up')}
                                tooltip="Flyt spørgsmål op"
                                disabled={props.disableEdit} />
                            <ToolBarButton
                                icon="interface_actions_delete"
                                variant="destructive-text"
                                onClick={handleDeleteQuestion}
                                tooltip="Slet spørgsmål"
                                disabled={props.disableEdit} />
                        </Flex>
                    }
                </Flex>
            </Flex>
        </ListRow>
    );
}